import {
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    where,
    getDocs,
    doc,
    updateDoc,
    serverTimestamp,
    limit
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Generate a short, memorable handle
export const generateHandle = () => {
    const adjectives = ['cool', 'smart', 'happy', 'bright', 'swift', 'bold', 'wise', 'kind', 'brave', 'calm'];
    const nouns = ['cat', 'dog', 'bird', 'fish', 'lion', 'tiger', 'bear', 'wolf', 'eagle', 'fox'];
    const numbers = Math.floor(Math.random() * 100);

    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];

    return `${adjective}${noun}${numbers}`;
};

// Check if handle is available
export const isHandleAvailable = async (handle) => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('handle', '==', handle));
        const querySnapshot = await getDocs(q);
        return querySnapshot.empty;
    } catch (error) {
        console.error('Error checking handle availability:', error);
        return false;
    }
};

// Create or update user
export const createUser = async (name, handle, code, email = null) => {
    try {
        const userData = {
            name,
            handle,
            code,
            email: email || `${handle}@goodvibes.app`,
            isOnline: true,
            lastSeen: serverTimestamp(),
            createdAt: serverTimestamp(),
            profilePicture: 'default',
            status: 'online'
        };

        const usersRef = collection(db, 'users');
        const docRef = await addDoc(usersRef, userData);

        return {
            id: docRef.id,
            ...userData
        };
    } catch (error) {
        console.error('Error creating user:', error);
        throw error;
    }
};

// Update user online status
export const updateUserStatus = async (userId, isOnline, profilePicture = null, status = null) => {
    try {
        const userRef = doc(db, 'users', userId);
        const updateData = {
            isOnline,
            lastSeen: serverTimestamp()
        };

        if (profilePicture !== null) {
            updateData.profilePicture = profilePicture;
        }

        if (status !== null) {
            updateData.status = status;
        }

        await updateDoc(userRef, updateData);
    } catch (error) {
        console.error('Error updating user status:', error);
    }
};

// Update user status (for manual status changes)
export const updateUserStatusOnly = async (userId, status) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            status: status,
            lastSeen: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating user status only:', error);
    }
};

// Set user online (login) - resets status to online
export const setUserOnline = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            isOnline: true,
            status: 'online',
            lastSeen: serverTimestamp()
        });
    } catch (error) {
        console.error('Error setting user online:', error);
    }
};

// Set user offline (logout)
export const setUserOffline = async (userId) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            isOnline: false,
            status: 'offline',
            lastSeen: serverTimestamp()
        });
    } catch (error) {
        console.error('Error setting user offline:', error);
    }
};

// Update user name
export const updateUserName = async (userId, newName) => {
    try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            name: newName,
            lastSeen: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating user name:', error);
        throw error;
    }
};

// Send a message
export const sendMessage = async (senderId, senderHandle, targetHandle, message, transformedMessage) => {
    try {
        const messagesRef = collection(db, 'messages');
        const messageData = {
            senderId,
            senderHandle,
            targetHandle,
            originalMessage: message,
            transformedMessage,
            timestamp: serverTimestamp(),
            isTransformed: true
        };

        await addDoc(messagesRef, messageData);
    } catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
};

// Listen to messages between two users
export const listenToMessages = (currentHandle, targetHandle, callback) => {
    console.log('Setting up message listener for:', currentHandle, 'and', targetHandle);

    const messagesRef = collection(db, 'messages');

    // Simple query to get all messages and filter in JavaScript
    const q = query(
        messagesRef,
        orderBy('timestamp', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
        console.log('Received snapshot with', snapshot.size, 'documents');
        const messages = [];
        snapshot.forEach((doc) => {
            const data = doc.data();
            // Filter messages that involve both users
            if ((data.senderHandle === currentHandle && data.targetHandle === targetHandle) ||
                (data.senderHandle === targetHandle && data.targetHandle === currentHandle)) {
                messages.push({
                    id: doc.id,
                    ...data
                });
            }
        });
        console.log('Filtered messages:', messages.length);
        callback(messages);
    }, (error) => {
        console.error('Error in message listener:', error);
    });
};

// Get user by handle
export const getUserByHandle = async (handle) => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('handle', '==', handle));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            return {
                id: doc.id,
                ...doc.data()
            };
        }
        return null;
    } catch (error) {
        console.error('Error getting user by handle:', error);
        return null;
    }
};

// Get all users (for user list) - excludes hidden users
export const getAllUsers = async () => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(
            usersRef,
            orderBy('lastSeen', 'desc')
        );

        const querySnapshot = await getDocs(q);
        const users = [];

        querySnapshot.forEach((doc) => {
            const userData = doc.data();
            // Only include users who are not hidden
            if (userData.status !== 'hidden') {
                users.push({
                    id: doc.id,
                    ...userData
                });
            }
        });

        return users;
    } catch (error) {
        console.error('Error getting all users:', error);
        return [];
    }
};

// Get recent users (for discovery) - excludes hidden users
export const getRecentUsers = async (limitCount = 10) => {
    try {
        const usersRef = collection(db, 'users');
        const q = query(
            usersRef,
            where('isOnline', '==', true),
            orderBy('lastSeen', 'desc'),
            limit(limitCount)
        );

        const querySnapshot = await getDocs(q);
        const users = [];

        querySnapshot.forEach((doc) => {
            const userData = doc.data();
            // Only include users who are not hidden
            if (userData.status !== 'hidden') {
                users.push({
                    id: doc.id,
                    ...userData
                });
            }
        });

        return users;
    } catch (error) {
        console.error('Error getting recent users:', error);
        return [];
    }
};
